/**
 * MatchSession — owns one match end to end.
 *
 *   sim (fixed 1/30 s steps, time-scalable) → replay buffer → booth → bot → grader
 *   render frame: interpolate prev/cur snapshot → crew → world → multiview → graphics → console → audio
 *
 * Two modes:
 *  - 'play'    full truck console (PGM/PVW/wall), graphics, booth, grading, audio, keyboard.
 *  - 'attract' title-screen backdrop: muted sim + bot director, PROGRAM feed only, full-window.
 *
 * Everything created here is torn down by dispose() (WebGL resources, DOM, listeners,
 * mixer sound bindings) so matches don't leak into each other.
 */
import * as THREE from 'three';
import type {
  CameraDef, CommentatorRequest, DirectorState, GradeFeedback, GradeReport, MatchConfig, MatchEvent, MatchStats,
  ResultSummary, WorldSnapshot,
} from '../core/contracts';
import { createRng } from '../core/rng';
import { createFootballSim, type FootballSim } from '../sim/football';
import { createWorld, type World, type WorldQuality } from '../render/world';
import { kitFor } from '../render/cameras/kit';
import { createCameraCrew, type CameraCrew, type FeedSource } from '../render/cameras/operators';
import { deviceProfile } from '../render/deviceProfile';
import { createMultiview, wallQualityFor, type FeedLayout, type Multiview } from '../render/cameras/multiview';
import {
  applyDirectorAction, createEmptySnapshot, createMixer, createReplayBuffer, lerpSnapshotInto, replayAnglePicker,
  type ActionContext, type DirectorAction, type Mixer, type ReplayBuffer,
} from '../director';
import { bindDirectorKeys } from '../director/input';
import { createGraphicsLayer, type GraphicsLayer } from '../graphics';
import { bindMixerSounds, type AudioEngine } from '../audio';
import {
  BOOTH_LATE_WINDOW_S, baseAudienceFor, camKindFromKit, createBooth, createBotDirector, createGrader, letterFor,
  type Booth, type BotDirector, type SportsGrader,
} from '../grading';
import { createConsole, type ConsoleCam, type TruckConsole } from '../ui/console';
import type { HalfTimeParams } from '../ui/screens';
import type { MatchExtras, StartMatchOptions } from '../meta/progress';
import type { Settings } from '../meta/settings';
import { createHighlightsRecorder, type HighlightsData, type HighlightsRecorder } from '../highlights/recorder';

export const SIM_STEP = 1 / 30;
/** Sim seconds to let the half-time whistle breathe before the interstitial. */
const HALF_TIME_HOLD_S = 2.5;
/** Sim seconds of full-time scenes before the report card. */
const FULL_TIME_HOLD_S = 4;

export type SessionMode = 'play' | 'attract';

export interface SessionOptions {
  mode: SessionMode;
  config: MatchConfig;
  renderer: THREE.WebGLRenderer;
  settings: Settings;
  /** Console container (play mode). */
  uiRoot?: HTMLElement;
  audio?: AudioEngine | null;
  start?: Partial<StartMatchOptions>;
  /** Let the bot director drive (attract mode always uses one). */
  bot?: { skill: number } | null;
  /** Sim time multiplier (testing). */
  timeScale?: number;
  /** Extra gate for keyboard input (e.g. !screens.isOpen()). */
  inputEnabled?: () => boolean;
  onHalfTime?(p: HalfTimeParams): void;
  onFullTime?(summary: ResultSummary, extras: MatchExtras): void;
  onPauseRequest?(): void;
  /** 'scripted': the booth generates nothing on its own; requests come from injectRequest() (training). */
  booth?: 'auto' | 'scripted';
}

export interface SessionStats {
  mode: SessionMode;
  fps: number;
  frameMs: number;
  fpsSamples: number[];
  sceneRenders: number;
  simT: number;
  matchClock: number;
  phase: string;
  score: { home: number; away: number };
  rating: number;
  letter: string;
  averageRating: number;
  viewers: number;
  cuts: number;
  replaysRolled: number;
  graphicsShown: number;
  toasts: number;
  requests: number;
  state: string;
  program: string;
  preview: string;
  replayActive: boolean;
  /** CPU ms (EMA): per sim step, crew update, multiview render submit, console/graphics/audio, whole frame. */
  perf: Record<string, number>;
}

export interface MatchSession {
  readonly config: MatchConfig;
  readonly mode: SessionMode;
  start(): void;
  setPaused(p: boolean): void;
  readonly paused: boolean;
  /** Continue after the half-time interstitial. */
  resumeSecondHalf(): void;
  applySettings(s: Settings): void;
  /** Re-run layout + render a frame (e.g. after a resize while frozen). */
  invalidate(): void;
  stats(): SessionStats;
  /** Current report (grader + session extras). */
  report(): GradeReport;
  dispatch(a: DirectorAction): boolean;
  /** Debug: synchronously render `frames` frames (gl.finish each) and return avg GPU+CPU ms per frame. */
  bench(frames?: number): number;
  /** Recorded highlight clips + on-air cut timeline (play mode; complete at full time). */
  highlights(): HighlightsData | null;
  /** Sim speed multiplier; 0 freezes the match (replays/transitions still run in real time). */
  setTimeScale(x: number): void;
  readonly timeScale: number;
  /** Subscribe to grader feedback (every toast). Returns an unsubscribe. */
  onFeedback(fn: (f: GradeFeedback) => void): () => void;
  /** Live read-only views for overlays (training producer, coaching). */
  director(): Readonly<DirectorState>;
  snapshot(): Readonly<WorldSnapshot>;
  events(): readonly MatchEvent[];
  /** Add a booth request (graded like any other). Returns it. */
  injectRequest(text: string, wants: CommentatorRequest['wants'], seconds: number): CommentatorRequest | null;
  dispose(): void;
}

const QUALITY_MAP: Record<Settings['quality'], WorldQuality> = { low: 'low', med: 'medium', high: 'high' };
const WALL_INTERVAL: Record<WorldQuality, number> = { low: 4, medium: 3, high: 3 };
/** Crowd density on the small monitor-wall tiles (relative to the quality setting). */
const WALL_CROWD_LOD = 0.3;
const NOT_PLAYING = new Set(['pre_match', 'half_time', 'full_time']);

export function pixelRatioFor(q: Settings['quality'], mode: SessionMode): number {
  const dpr = window.devicePixelRatio || 1;
  if (q === 'low') return 1;
  // Capped by device (≤1.5 even on strong retina GPUs: 60 fps beats 2× pixels across ~13 feeds).
  const cap = deviceProfile().maxPixelRatio;
  if (mode === 'attract') return Math.min(dpr, 1.25, cap);
  return Math.min(dpr, cap, q === 'high' ? 1.5 : 1.25);
}

export function createMatchSession(o: SessionOptions): MatchSession {
  const { config, renderer, mode } = o;
  const play = mode === 'play';
  let timeScale = Math.max(0.1, o.timeScale ?? 1);
  let settings = o.settings;
  const disposers: (() => void)[] = [];

  // ------------------------------------------------------------------ core objects
  const sim: FootballSim = createFootballSim(config);
  const unlocked = o.start?.unlockedCameras ?? [];
  const kit: CameraDef[] = play ? kitFor(unlocked) : kitFor([]);
  const camKindOf = camKindFromKit(kit);
  const world: World = createWorld(config);
  world.setQuality(mode === 'attract' ? (settings.quality === 'high' ? 'medium' : QUALITY_MAP[settings.quality]) : QUALITY_MAP[settings.quality]);
  const crew: CameraCrew = createCameraCrew(kit, { anchors: world.anchors, seed: config.seed });
  const buffer: ReplayBuffer = createReplayBuffer({ seconds: 90 });
  const recorder: HighlightsRecorder | null = play ? createHighlightsRecorder(config, kit) : null;

  let prev: WorldSnapshot = sim.snapshot();
  let cur: WorldSnapshot = prev;
  const liveInterp = createEmptySnapshot(cur.players.length);
  const replaySnap = createEmptySnapshot(cur.players.length);
  buffer.push(cur);

  const mixer: Mixer = createMixer(kit.map((c) => c.id), {
    now: () => cur.t,
    events: () => sim.events,
    pickReplayAngle: replayAnglePicker(kit),
    initialProgram: 'CAM 1',
    initialPreview: 'CAM 2',
  });
  disposers.push(mixer.on('instruction', ({ camId, instruction }) => crew.setInstruction(camId, instruction)));
  disposers.push(mixer.on('replayEnd', () => { if (!mixer.transition) crew.endReplay(); }));
  disposers.push(mixer.on('transitionEnd', (t) => { if (t.fromReplay && !t.toReplay) crew.endReplay(); }));

  const difficulty = o.start?.difficulty ?? 'pro';
  const booth: Booth | null = play ? createBooth(config, createRng((config.seed ^ 0xb007) >>> 0), { difficulty }) : null;
  const scriptedBooth = o.booth === 'scripted';
  const injected: CommentatorRequest[] = [];
  const allRequests = (): CommentatorRequest[] => (injected.length ? [...(booth!.all() as CommentatorRequest[]), ...injected] : booth!.all() as CommentatorRequest[]);
  const openRequests = (t: number): CommentatorRequest[] => (injected.length
    ? [...booth!.open(t), ...injected.filter((r) => !r.resolved && t <= r.expiresAt + BOOTH_LATE_WINDOW_S)]
    : booth!.open(t));
  const grader: SportsGrader | null = play
    ? createGrader(config, {
      difficulty,
      camKindOf,
      baseAudience: o.start?.baseAudience ?? config.baseAudience ?? baseAudienceFor(config.competition),
      onBoothResolved: (id, st) => {
        booth?.resolve(id, st);
        const r = (booth?.all().find((x) => x.id === id) ?? injected.find((x) => x.id === id)) as CommentatorRequest | undefined;
        if (r) r.resolved = st;
      },
    })
    : null;
  const botSkill = o.bot?.skill ?? (mode === 'attract' ? 0.92 : null);
  const bot: BotDirector | null = botSkill !== null ? createBotDirector(botSkill, createRng((config.seed ^ 0xb07) >>> 0), { camKindOf }) : null;

  // ------------------------------------------------------------------ console / graphics (play)
  let ui: TruckConsole | null = null;
  let gfx: GraphicsLayer | null = null;
  const hotkeyIds = kit.filter((c) => /^CAM [1-9]$/.test(c.id)).map((c) => c.id);
  if (play) {
    if (!o.uiRoot) throw new Error('session: play mode needs uiRoot');
    const cams: ConsoleCam[] = kit.map((c) => {
      const n = hotkeyIds.indexOf(c.id);
      const cc: ConsoleCam = { id: c.id, label: c.label, kind: c.kind };
      if (n >= 0) cc.hotkey = String(n + 1);
      return cc;
    });
    ui = createConsole(o.uiRoot, { cams, punchMode: settings.punchMode });
    gfx = createGraphicsLayer(ui.programOverlay, config);
    disposers.push(() => { gfx?.dispose(); ui?.destroy(); });
    disposers.push(ui.onLayout(() => { needsRender = true; }));
  }

  // ------------------------------------------------------------------ multiview
  let posed: FeedSource = 'live';
  let crowdLod = 1;
  let alpha = 0;
  let replayValid = false;
  const multiview: Multiview = createMultiview(renderer, world.scene, crew, {
    wall: { ...wallQualityFor(world.quality), interval: WALL_INTERVAL[world.quality] },
    onBeforeFeed: (feed) => {
      const lod = feed.tier === 'wall' ? WALL_CROWD_LOD : 1;
      if (lod !== crowdLod) { world.setCrowdLod(lod); crowdLod = lod; }
      const want: FeedSource = feed.source === 'replay' && replayValid ? 'replay' : 'live';
      if (want === posed) return;
      if (want === 'replay') world.update(replaySnap, replaySnap, 0, 0);
      else world.update(prev, cur, alpha, 0);
      posed = want;
    },
  });

  // ------------------------------------------------------------------ action routing
  const actionCtx: ActionContext = {
    buffer,
    events: () => sim.events,
    snapshot: () => cur,
    onBoothFocus: (dir) => { ui?.focusBooth(dir); },
    requests: () => (booth ? openRequests(cur.t) : []),
    boothFocusId: () => ui?.focusedRequest() ?? null,
  };
  let replaysRolled = 0;
  let graphicsShown = 0;
  disposers.push(mixer.on('replayStart', () => { replaysRolled++; }));
  disposers.push(mixer.on('graphicShown', (g) => { if (g.kind !== 'scorebug') graphicsShown++; }));

  let state: 'live' | 'halftime' | 'ended' = 'live';
  let paused = false;
  let disposed = false;

  const dispatch = (a: DirectorAction): boolean => {
    if (disposed || state === 'ended') return false;
    return applyDirectorAction(mixer, a, actionCtx);
  };

  if (ui) {
    const acts = ui.actions;
    disposers.push(
      acts.on('punch', (camId) => { dispatch(mixer.state.replay.active ? { type: 'replay_angle', camId } : { type: 'hot_punch', camId }); }),
      acts.on('preview', (camId) => { dispatch({ type: 'preview', camId }); }),
      acts.on('take', () => { dispatch({ type: 'take' }); }),
      acts.on('mix', () => { dispatch({ type: 'mix' }); }),
      acts.on('replay', () => { dispatch({ type: 'replay_toggle' }); }),
      acts.on('graphic', (g) => { dispatch(g === 'lower_third' ? { type: 'graphic_context' } : { type: 'graphic_toggle', kind: g }); }),
      acts.on('clearGraphics', () => { dispatch({ type: 'graphics_clear' }); }),
      acts.on('scorebug', () => { dispatch({ type: 'scorebug_toggle' }); }),
      acts.on('pause', () => { o.onPauseRequest?.(); }),
    );
  }

  if (play) {
    const keysEnabled = () => !disposed && !paused && state === 'live' && (o.inputEnabled?.() ?? true);
    disposers.push(bindDirectorKeys((a) => {
      if (a.type === 'replay_end' && !mixer.state.replay.active) { o.onPauseRequest?.(); return; }
      if (settings.punchMode === 'preview') {
        if (a.type === 'hot_punch') a = { type: 'preview', camId: a.camId };
        else if (a.type === 'preview') a = { type: 'hot_punch', camId: a.camId };
      }
      dispatch(a);
    }, { camIds: hotkeyIds, isReplayActive: () => mixer.state.replay.active, enabled: keysEnabled }));
    const onKey = (e: KeyboardEvent) => {
      if (!keysEnabled() || e.repeat || e.ctrlKey || e.metaKey || e.altKey) return;
      ui?.indicate(e.code === 'Space' ? 'SPACE' : e.code === 'Escape' ? 'ESCAPE' : e.code);
    };
    window.addEventListener('keydown', onKey);
    disposers.push(() => window.removeEventListener('keydown', onKey));
  }
  if (play && o.audio) disposers.push(bindMixerSounds(mixer, o.audio));

  // ------------------------------------------------------------------ match bookkeeping
  let acc = 0;
  let stats: MatchStats = sim.stats();
  let statsAge = 0;
  let cachedReport: GradeReport | null = grader ? grader.report() : null;
  let reportAge = 0;
  let ratingSum = 0, ratingN = 0, ratingClock = 0;
  let bestStreak = 0;
  let halfTimeAt = -1, halfTimeShown = false;
  let fullTimeAt = -1;
  let toastCount = 0;
  const perf = { step: 0, steps: 0, crew: 0, render: 0, ui: 0, frame: 0 };
  const frameEvents: MatchEvent[] = [];
  let lastReplayEvent: MatchEvent | null = null;

  const averageRating = (): number => (ratingN > 0 ? ratingSum / ratingN : cachedReport?.rating ?? 0);

  const fullReport = (): GradeReport => {
    const r = grader!.report();
    const avg = Math.round(averageRating() * 10) / 10;
    bestStreak = Math.max(bestStreak, r.streak);
    return {
      ...r,
      rating: avg,
      letter: letterFor(avg),
      matchAverageRating: avg,
      multiplier: grader!.multiplier(),
      peakViewers: grader!.peakViewers(),
      bestStreak,
    };
  };

  const feedbackListeners = new Set<(f: GradeFeedback) => void>();
  const onToast = (f: GradeFeedback) => {
    toastCount++;
    for (const fn of feedbackListeners) { try { fn(f); } catch (e) { console.warn('[session] feedback listener', e); } }
    recorder?.feedback(f);
    ui?.toast(f);
    if (o.audio && (f.severity === 'great' || f.severity === 'terrible')) o.audio.ui(f.severity === 'great' ? 'good' : 'bad');
  };

  function stepOnce(): void {
    const t0 = performance.now();
    stepOnceInner();
    perf.step = perf.step * 0.95 + (performance.now() - t0) * 0.05;
  }

  function stepOnceInner(): void {
    const evs = sim.step(SIM_STEP);
    prev = cur;
    cur = sim.snapshot();
    buffer.push(cur);
    recorder?.record(cur, evs, mixer.state);
    if (evs.length) { frameEvents.push(...evs); gfx?.pushEvents(evs); }

    const frames = crew.frames();
    if (!scriptedBooth) booth?.update(cur, evs, cur.t);
    if (bot) {
      const acts = bot.decide({ snapshot: cur, newEvents: evs, frames, requests: booth ? booth.active(cur.t) : [], director: mixer.state });
      for (const a of acts) dispatch(a);
    }
    if (grader) {
      const d = mixer.state;
      const programFrame = d.replay.active ? null : crew.getFrame(d.programCamId);
      const fb = grader.tick({ dt: SIM_STEP, snapshot: cur, newEvents: evs, director: d, programFrame, allFrames: frames, requests: openRequests(cur.t) });
      for (const f of fb) onToast(f);
      reportAge += SIM_STEP;
      if (reportAge >= 0.1) { reportAge = 0; cachedReport = grader.report(); bestStreak = Math.max(bestStreak, cachedReport.streak); }
      if (!NOT_PLAYING.has(cur.phase)) {
        ratingClock += SIM_STEP;
        if (ratingClock >= 1) { ratingClock -= 1; ratingSum += cachedReport!.rating; ratingN++; }
      }
    }
    statsAge += SIM_STEP;
    if (statsAge >= 0.5) { statsAge = 0; stats = sim.stats(); }

    // half time
    if (cur.phase === 'half_time' && halfTimeAt < 0) halfTimeAt = cur.t;
    if (halfTimeAt >= 0 && !halfTimeShown && cur.t - halfTimeAt >= HALF_TIME_HOLD_S) {
      halfTimeShown = true;
      if (play) {
        state = 'halftime';
        o.audio?.setPaused(true);
        const r = fullReport();
        o.onHalfTime?.({
          report: r, homeName: config.home.shortName, awayName: config.away.shortName,
          score: { ...cur.score }, homeColor: config.home.primaryColor, awayColor: config.away.primaryColor,
        });
      }
    }
    // full time
    // (sim time stops at the final whistle, so count steps for the hold)
    if (sim.finished) fullTimeAt = fullTimeAt < 0 ? 0 : fullTimeAt + SIM_STEP;
    if (fullTimeAt >= FULL_TIME_HOLD_S && state === 'live') {
      state = 'ended';
      if (play) o.audio?.setPaused(true);
      const summary: ResultSummary = {
        sport: 'football', seed: config.seed,
        config: { competition: config.competition, venue: config.venue, weather: config.weather, timeOfDay: config.timeOfDay },
        homeName: config.home.name, awayName: config.away.name, score: { ...cur.score },
        report: grader ? fullReport() : emptyReport(),
        cuts: mixer.state.cuts.slice(),
        playedAt: new Date().toISOString(),
      };
      const extras: MatchExtras = ui ? { ...ui.stats() } : {};
      extras.peakViewers = Math.max(extras.peakViewers ?? 0, grader?.peakViewers() ?? 0);
      extras.bestStreak = Math.max(extras.bestStreak ?? 0, bestStreak);
      // Defer so callbacks can dispose this session safely.
      setTimeout(() => o.onFullTime?.(summary, extras), 0);
    }
  }

  // ------------------------------------------------------------------ rendering
  let needsRender = true;
  let lastSceneRenders = 0;
  const layoutList: FeedLayout[] = [];

  function render(simDt: number): void {
    const d = mixer.state;
    const tr = mixer.transition;

    // which feed is on PGM (stinger swaps the source under the wipe at 50%)
    let pgmCam = d.replay.active ? d.replay.camId : d.programCamId;
    let pgmSrc: FeedSource = d.replay.active ? 'replay' : 'live';
    let blend: FeedLayout['blend'];
    if (tr) {
      if (tr.kind === 'stinger' && tr.progress < 0.5) { pgmCam = tr.from; pgmSrc = tr.fromReplay ? 'replay' : 'live'; }
      else if (tr.kind === 'mix') blend = { camId: tr.from, source: tr.fromReplay ? 'replay' : 'live', weight: 1 - tr.progress };
    }

    // replay snapshot + operator
    const needReplay = pgmSrc === 'replay' || blend?.source === 'replay';
    replayValid = false;
    if (needReplay && buffer.size > 1) {
      const headT = d.replay.active || d.replay.toT > 0 ? d.replay.headT : cur.t;
      if (buffer.interpolate(headT, replaySnap)) {
        replayValid = true;
        const evId = d.replay.eventId;
        if (evId !== null && lastReplayEvent?.id !== evId) lastReplayEvent = sim.events.find((e) => e.id === evId) ?? null;
        const ev = evId === null ? null : lastReplayEvent;
        crew.replayCamera(pgmSrc === 'replay' ? pgmCam : blend!.camId, replaySnap, { event: ev });
      }
    }

    // pose world live (advances world clocks once per frame)
    world.update(prev, cur, alpha, simDt);
    posed = 'live';

    layoutList.length = 0;
    if (ui) {
      const l = ui.layout();
      const p: FeedLayout = { camId: pgmCam, rect: l.program, tier: 'program', source: pgmSrc };
      if (blend) p.blend = blend;
      layoutList.push(p);
      layoutList.push({ camId: d.previewCamId, rect: l.preview, tier: 'preview', source: 'live' });
      for (const w of l.wall) layoutList.push({ camId: w.camId, rect: w.rect, tier: 'wall', source: 'live' });
    } else {
      const p: FeedLayout = { camId: pgmCam, rect: { x: 0, y: 0, w: window.innerWidth, h: window.innerHeight }, tier: 'program', source: pgmSrc };
      if (blend) p.blend = blend;
      layoutList.push(p);
    }
    const st = multiview.render(layoutList);
    lastSceneRenders = st.sceneRenders;
    needsRender = false;
  }

  // ------------------------------------------------------------------ frame loop
  let raf = 0;
  let last = 0;
  const fpsWindow: number[] = [];
  const fpsSamples: number[] = [];
  let fpsAcc = 0, fpsFrames = 0;
  let fps = 0, frameMs = 0;

  function frame(now: number): void {
    if (disposed) return;
    raf = requestAnimationFrame(frame);
    const realDt = last ? Math.min(0.1, Math.max(0, (now - last) / 1000)) : 0;
    last = now;
    fpsAcc += realDt; fpsFrames++;
    fpsWindow.push(realDt);
    if (fpsWindow.length > 60) fpsWindow.shift();
    if (fpsAcc >= 1) {
      fps = fpsFrames / fpsAcc;
      frameMs = (fpsAcc / fpsFrames) * 1000;
      fpsSamples.push(Math.round(fps * 10) / 10);
      if (fpsSamples.length > 600) fpsSamples.shift();
      fpsAcc = 0; fpsFrames = 0;
    }

    const advancing = !paused && state !== 'halftime';
    if (!advancing) {
      if (needsRender) render(0);
      return;
    }

    const simDt = realDt * timeScale;
    frameEvents.length = 0;
    if (state === 'live' || state === 'ended') {
      acc += simDt;
      const maxSteps = Math.ceil(timeScale * 4) + 2;
      let n = 0;
      perf.steps = 0;
      while (acc >= SIM_STEP && n < maxSteps) {
        stepOnce();
        acc -= SIM_STEP;
        n++;
        perf.steps = n;
        if ((state as string) === 'halftime') { acc = 0; break; }
      }
      if (n >= maxSteps) acc = Math.min(acc, SIM_STEP);
    }
    alpha = Math.max(0, Math.min(1, acc / SIM_STEP));

    // director clocks: transitions/graphics in real time, replay head in sim time
    mixer.update(realDt);
    if (timeScale > 1) mixer.updateReplay(realDt * (timeScale - 1));

    const tc = performance.now();
    lerpSnapshotInto(liveInterp, prev, cur, alpha);
    crew.update(liveInterp, simDt, frameEvents);
    const tr = performance.now();
    render(simDt);
    const tu = performance.now();

    if (ui && grader) {
      const live = cachedReport ?? grader.report();
      // The rating dial shows the running match average — the number the half-time card and the
      // report grade you on — so the HUD letter never disagrees with the final letter.
      const avg = Math.round(averageRating() * 10) / 10;
      const view: GradeReport = { ...live, rating: avg, letter: letterFor(avg) };
      ui.update({ snapshot: cur, report: view, director: mixer.state, requests: allRequests(), config });
      gfx!.sync(mixer.state, cur, stats, mixer.state.replay);
    }
    if (play && o.audio) o.audio.update(cur, frameEvents, simDt);
    const te = performance.now();
    const k = 0.05;
    perf.crew += (tr - tc - perf.crew) * k;
    perf.render += (tu - tr - perf.render) * k;
    perf.ui += (te - tu - perf.ui) * k;
    perf.frame += (te - now - perf.frame) * k;
  }

  // Settle the operators on the opening frame.
  crew.update(cur, SIM_STEP, []);

  const session: MatchSession = {
    config,
    mode,
    get paused() { return paused; },
    start() {
      if (raf || disposed) return;
      renderer.setPixelRatio(pixelRatioFor(settings.quality, mode));
      if (play) o.audio?.setPaused(false);
      raf = requestAnimationFrame(frame);
    },
    setPaused(p) {
      if (paused === p) return;
      paused = p;
      last = 0;
      if (play) o.audio?.setPaused(p || state !== 'live');
    },
    resumeSecondHalf() {
      if (state !== 'halftime') return;
      state = 'live';
      last = 0;
      if (play) o.audio?.setPaused(paused);
    },
    applySettings(s) {
      settings = s;
      const q = QUALITY_MAP[s.quality];
      if (mode === 'play' && world.quality !== q) { world.setQuality(q); crowdLod = 1; multiview.setWallQuality({ ...wallQualityFor(q), interval: WALL_INTERVAL[q] }); }
      renderer.setPixelRatio(pixelRatioFor(s.quality, mode));
      ui?.setPunchMode(s.punchMode);
      o.audio?.setMaster(s.masterVolume);
      multiview.invalidateWall();
      needsRender = true;
    },
    invalidate() { needsRender = true; multiview.invalidateWall(); },
    stats() {
      const r = cachedReport;
      return {
        mode, fps: Math.round(fps * 10) / 10, frameMs: Math.round(frameMs * 10) / 10, fpsSamples: fpsSamples.slice(),
        sceneRenders: lastSceneRenders, simT: Math.round(cur.t * 10) / 10, matchClock: Math.round(cur.matchClock), phase: cur.phase,
        score: { ...cur.score }, rating: r?.rating ?? 0, letter: r?.letter ?? '-', averageRating: Math.round(averageRating() * 10) / 10,
        viewers: r?.viewers ?? 0, cuts: mixer.state.cuts.length, replaysRolled, graphicsShown, toasts: toastCount,
        requests: booth?.all().length ?? 0, state: paused ? 'paused' : state, program: mixer.state.programCamId,
        preview: mixer.state.previewCamId, replayActive: mixer.state.replay.active,
        perf: Object.fromEntries(Object.entries(perf).map(([kk, v]) => [kk, Math.round(v * 100) / 100])),
      };
    },
    report: () => (grader ? fullReport() : emptyReport()),
    highlights: () => (recorder ? recorder.finish(mixer.state.cuts, grader ? fullReport() : null) : null),
    dispatch,
    get timeScale() { return timeScale; },
    setTimeScale(x) { timeScale = Math.max(0, Math.min(16, Number.isFinite(x) ? x : 1)); },
    onFeedback(fn) { feedbackListeners.add(fn); return () => { feedbackListeners.delete(fn); }; },
    director: () => mixer.state,
    snapshot: () => cur,
    events: () => sim.events,
    injectRequest(text, wants, seconds) {
      if (!booth) return null;
      const r: CommentatorRequest = { id: 10_000 + injected.length, t: cur.t, text, wants, expiresAt: cur.t + seconds };
      injected.push(r);
      return r;
    },
    bench(frames = 30) {
      const gl = renderer.getContext();
      const px = new Uint8Array(4);
      const sync = () => gl.readPixels(0, 0, 1, 1, gl.RGBA, gl.UNSIGNED_BYTE, px); // forces a GPU round-trip
      render(0); sync();
      const t0 = performance.now();
      for (let i = 0; i < frames; i++) { needsRender = true; render(0); sync(); }
      return (performance.now() - t0) / frames;
    },
    dispose() {
      if (disposed) return;
      disposed = true;
      cancelAnimationFrame(raf);
      feedbackListeners.clear();
      for (const d of disposers.splice(0)) { try { d(); } catch (e) { console.warn('[session] dispose', e); } }
      multiview.dispose();
      world.dispose();
      buffer.clear();
      renderer.renderLists.dispose();
      // Clear the canvas so the next owner starts from black.
      renderer.setRenderTarget(null);
      renderer.setScissorTest(false);
      renderer.clear(true, true, true);
    },
  };
  return session;
}

function emptyReport(): GradeReport {
  return {
    total: 0, rating: 0, letter: 'F', viewers: 0, streak: 0, feedback: [], keyMoments: [],
    byCategory: { coverage: 0, shot_choice: 0, pacing: 0, continuity: 0, replays: 0, graphics: 0, booth: 0 },
  };
}
